import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X } from "lucide-react";

const SHOWN_KEY = "kk_promo_shown";
const DISMISSED_KEY = "kk_promo_dismissed";

export const WelcomePromoModal = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [coupon, setCoupon] = useState(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Decide if modal should ever appear in this session.
  useEffect(() => {
    if (user) return; // signed-in users don't need the prompt
    if (localStorage.getItem(DISMISSED_KEY) === "1") return;
    if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/support"))
      return;
    if (location.pathname === "/login" || location.pathname === "/register") return;

    const alreadyShown = sessionStorage.getItem(SHOWN_KEY) === "1";
    const delay = alreadyShown ? 0 : 6000;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/coupons/welcome");
        if (data && data.code) {
          setCoupon(data);
          setOpen(true);
          sessionStorage.setItem(SHOWN_KEY, "1");
        }
      } catch {}
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [user, location.pathname]);

  const close = (forever = false) => {
    setOpen(false);
    if (forever) localStorage.setItem(DISMISSED_KEY, "1");
  };

  const copy = async () => {
    if (!coupon) return;
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (!open || !coupon) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#1a1918]/60 backdrop-blur-sm pop-in"
      data-testid="welcome-promo"
      onClick={() => close(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#fdfbf7] max-w-md w-full overflow-hidden border border-[#ebe5df] shadow-2xl"
      >
        <div
          className="aspect-[16/9] bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=1200&q=70)",
          }}
        >
          <div className="w-full h-full bg-gradient-to-t from-[#1a1918] via-transparent to-transparent" />
        </div>
        <button
          onClick={() => close(false)}
          className="absolute top-3 right-3 w-9 h-9 bg-white/85 backdrop-blur flex items-center justify-center hover:bg-white"
          data-testid="welcome-close"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="p-8">
          <div className="flex items-center gap-2 overline">
            <Sparkles size={14} className="text-[#c58b76]" />
            Welcome offer
          </div>
          <h2 className="font-serif text-4xl mt-3 leading-tight">
            10% off your<br />first order.
          </h2>
          <p className="text-sm text-[#5c5a56] mt-4 leading-relaxed">
            {coupon.description ||
              "Create an account to claim your code. We'll send it to your bag."}
          </p>

          <div className="mt-6 border-2 border-dashed border-[#c58b76] bg-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="overline">Your code</div>
              <div className="font-serif text-2xl tracking-wider mt-0.5" data-testid="welcome-code">
                {coupon.code}
              </div>
            </div>
            <button
              onClick={copy}
              className="overline text-[#c58b76] hover:text-[#1a1918]"
              data-testid="welcome-copy"
            >
              {copied ? "Copied ✓" : "Copy"}
            </button>
          </div>

          <Link
            to="/register"
            onClick={() => close(false)}
            className="btn-primary w-full justify-center mt-6"
            data-testid="welcome-signup-cta"
          >
            Create my account
          </Link>
          <Link
            to="/login"
            onClick={() => close(false)}
            className="block text-center text-xs uppercase tracking-[0.18em] mt-4 text-[#5c5a56] hover:text-[#1a1918]"
            data-testid="welcome-login-link"
          >
            Already a member? Sign in
          </Link>
          <button
            onClick={() => close(true)}
            className="block text-center w-full text-[11px] uppercase tracking-[0.16em] mt-4 text-[#a8a39d] hover:text-[#5c5a56]"
            data-testid="welcome-dismiss"
          >
            Don't show again
          </button>
        </div>
      </div>
    </div>
  );
};
